import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function ForTherapistsPage() {
  const [selectedBenefit, setSelectedBenefit] = useState(0);

  const benefits = [
    {
      title: 'Get Found by Ideal Clients',
      description: 'Stop marketing and start being discovered. Your profile appears when clients search for your exact specialties.',
      stat: 'More inquiries',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      )
    },
    {
      title: 'Help Clients Find You',
      description: 'Clients can filter by your specialties, insurance, location, and treatment approach to find the perfect match.',
      stat: 'Better client fit',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: 'Build Your Professional Presence',
      description: 'Detailed profiles help you stand out from other therapists in your area with your unique approach and specialties.',
      stat: 'Professional visibility',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      )
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Create Your Profile',
      description: 'Add your photo, credentials, specialties, and what makes your approach unique.',
      time: '10 minutes'
    },
    {
      number: '02',
      title: 'Add Your Details',
      description: 'Include your specialties, insurance accepted, and treatment approach so clients can find the right match.',
      time: '5 minutes'
    },
    {
      number: '03',
      title: 'Start Getting Clients',
      description: 'Your profile goes live and clients can find and book with you immediately.',
      time: 'Same day'
    }
  ];

  return (
    <>
      <SEOHead {...seoConfigs.forTherapists} />
      
      <div className="min-h-screen bg-gradient-to-br from-[#e4fde1] to-[#e4fde1]/70" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Hero Section */}
        <section className="py-20 lg:py-32">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side - Content */}
              <div>
                <div className="inline-flex items-center gap-2 bg-[#9dacff]/20 text-[#254e70] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-[#9dacff] rounded-full"></div>
                  For Licensed Therapists
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-[#254e70] mb-6 leading-tight">
                  Your Next Client is
                  <br />
                  <span className="text-[#9dacff]">Searching Right Now</span>
                </h1>
                
                <p className="text-xl text-[#254e70]/80 mb-8 leading-relaxed">
                  Join therapists who use TherapyList to connect with clients actively seeking their expertise. No cold calls, no marketing stress—just meaningful connections.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/join"
                    className="group inline-flex items-center gap-3 bg-[#254e70] text-white px-8 py-4 rounded-2xl font-semibold hover:bg-[#1d3e5a] transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Join Free Today
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>

                <div className="flex items-center gap-6 text-[#254e70]/60 text-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Free to start
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    10-minute setup
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    No contracts
                  </div>
                </div>

                {/* Login prompt for existing users */}
                <div className="mt-6 p-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50">
                  <p className="text-[#254e70]/80 text-sm">
                    Already have a TherapyList account?{' '}
                    <Link 
                      to="/login" 
                      className="text-[#9dacff] hover:text-[#254e70] font-medium hover:underline transition-colors duration-200"
                    >
                      Sign in to your dashboard
                    </Link>
                  </p>
                </div>
              </div>

              {/* Right side - Placeholder for image/graphic */}
              <div className="relative">
                <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/50">
                  <div className="aspect-square bg-gradient-to-br from-[#9dacff]/20 to-[#254e70]/20 rounded-2xl overflow-hidden">
                    <img 
                      src="http://cms.therapylist.com/wp-content/uploads/2025/07/Therapy-Profile.jpg"
                      alt="TherapyList Therapy Profile showing detailed therapist information"
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                </div>
                
                {/* Floating stats */}
                <div className="absolute -top-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-white/50">
                  <div className="text-2xl font-bold text-[#9dacff]">Growing</div>
                  <div className="text-[#254e70]/70 text-sm">Directory</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#254e70] mb-6">
                Why Therapists Love TherapyList
              </h2>
              <p className="text-lg text-[#254e70]/70 max-w-2xl mx-auto">
                Built by a therapist who understands the challenges of growing a practice.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Benefits list */}
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                      selectedBenefit === index 
                        ? 'bg-white/80 shadow-lg border border-[#9dacff]/30' 
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    onClick={() => setSelectedBenefit(index)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                        selectedBenefit === index 
                          ? 'bg-[#9dacff] text-white' 
                          : 'bg-[#e4fde1] text-[#254e70]'
                      }`}>
                        {benefit.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-[#254e70] mb-2">
                          {benefit.title}
                        </h3>
                        <p className="text-[#254e70]/80 leading-relaxed mb-3">
                          {benefit.description}
                        </p>
                        <div className="inline-flex items-center gap-2 bg-[#e4fde1] text-[#254e70] px-3 py-1 rounded-full text-sm font-medium">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          {benefit.stat}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Visual/placeholder for selected benefit */}
              <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/50">
                <div className="aspect-video bg-gradient-to-br from-[#9dacff]/10 to-[#254e70]/10 rounded-2xl overflow-hidden">
                  <img 
                    src="http://cms.therapylist.com/wp-content/uploads/2025/07/TherapyList-Therapist-Dashboard.jpg"
                    alt="TherapyList Therapist Dashboard showing profile management and client inquiries"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 lg:py-20 bg-white/30 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#254e70] mb-6">
                Get Started in 3 Simple Steps
              </h2>
              <p className="text-lg text-[#254e70]/70">
                Your profile can be live and attracting clients in under 20 minutes.
              </p>
            </div>

            <div className="space-y-8">
              {steps.map((step, index) => (
                <div key={index} className="flex items-center gap-8">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#9dacff] rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {step.number}
                  </div>
                  <div className="flex-1 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-semibold text-[#254e70]">
                        {step.title}
                      </h3>
                      <span className="text-[#9dacff] font-medium text-sm bg-[#9dacff]/10 px-3 py-1 rounded-full">
                        {step.time}
                      </span>
                    </div>
                    <p className="text-[#254e70]/80">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-[#254e70] to-[#1d3e5a] rounded-3xl p-8 lg:p-12 text-white">
              <h2 className="text-3xl lg:text-4xl font-semibold mb-6 text-[#e4fde1]">
                Ready to Grow Your Practice?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Join therapists who've already started growing their practices with TherapyList.
              </p>
              <Link 
                to="/join"
                className="group inline-flex items-center gap-3 bg-white text-[#254e70] px-8 py-4 rounded-2xl font-semibold hover:bg-white/90 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                Create Your Free Profile
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <p className="text-white/70 text-sm mt-4">
                Live in minutes • Cancel anytime
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default ForTherapistsPage;